// Talleres (Vehicle OS) para el portal web/escritorio.
// Usa las MISMAS rutas RTDB que la app móvil (users/{uid}/vehicleWorkshops),
// así la gestión de flota en escritorio y el móvil comparten los datos.
import { onValue, push, remove, set, get, off, ref } from 'firebase/database';
import { uploadBytesResumable, getDownloadURL, ref as stRef } from 'firebase/storage';

const workshopsRef = (db, uid) => ref(db, `users/${uid}/vehicleWorkshops`);
const workshopRef = (db, uid, workshopId) => ref(db, `users/${uid}/vehicleWorkshops/${workshopId}`);
const solutionsRef = (db, uid, workshopId) => ref(db, `users/${uid}/vehicleWorkshops/${workshopId}/solutions`);
const workshopImageRef = (storage, uid, workshopId) => stRef(storage, `users/${uid}/workshop_${workshopId}`);

export const WORKSHOP_BUSINESS_TYPES = [
  { id: 'mechanic', labelKey: 'workshop_type_mechanic' },
  { id: 'bodyshop', labelKey: 'workshop_type_bodyshop' },
  { id: 'tires', labelKey: 'workshop_type_tires' },
  { id: 'detailing', labelKey: 'workshop_type_detailing' },
  { id: 'electric', labelKey: 'workshop_type_electric' },
  { id: 'mobile', labelKey: 'workshop_type_mobile' },
  { id: 'other', labelKey: 'workshop_type_other' },
];

export const VEHICLE_SOLUTION_TYPES = [
  { id: 'oil', labelKey: 'workshop_solution_oil' },
  { id: 'brakes', labelKey: 'workshop_solution_brakes' },
  { id: 'tires', labelKey: 'workshop_solution_tires' },
  { id: 'battery', labelKey: 'workshop_solution_battery' },
  { id: 'alignment', labelKey: 'workshop_solution_alignment' },
  { id: 'diagnostics', labelKey: 'workshop_solution_diagnostics' },
  { id: 'ac', labelKey: 'workshop_solution_ac' },
  { id: 'wash', labelKey: 'workshop_solution_wash' },
  { id: 'detailing', labelKey: 'workshop_solution_detailing' },
  { id: 'electrical', labelKey: 'workshop_solution_electrical' },
  { id: 'engine', labelKey: 'workshop_solution_engine' },
  { id: 'other', labelKey: 'workshop_solution_other' },
];

const mapList = (val) => {
  if (!val) return [];
  return Object.keys(val).map((id) => ({ ...val[id], id }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};

export const subscribeWorkshops = (db, uid, onData) => {
  if (!db || !uid) {
    onData([]);
    return () => {};
  }
  const r = workshopsRef(db, uid);
  const handler = (snapshot) => onData(mapList(snapshot.val()));
  onValue(r, handler);
  return () => off(r, 'value', handler);
};

export const getWorkshop = async (db, uid, workshopId) => {
  if (!db || !uid || !workshopId) return null;
  const snap = await get(workshopRef(db, uid, workshopId));
  const val = snap.val();
  return val ? { ...val, id: workshopId } : null;
};

export const subscribeWorkshopSolutions = (db, uid, workshopId, onData) => {
  if (!db || !uid || !workshopId) {
    onData([]);
    return () => {};
  }
  const r = solutionsRef(db, uid, workshopId);
  const handler = (snapshot) => onData(mapList(snapshot.val()));
  onValue(r, handler);
  return () => off(r, 'value', handler);
};

export const saveWorkshop = async (db, uid, workshop, workshopId = null) => {
  if (!db || !uid) throw new Error('missing_uid');
  const payload = {
    name: workshop.name || '',
    businessType: workshop.businessType || 'mechanic',
    phone: workshop.phone || '',
    description: workshop.description || '',
    address: workshop.address || '',
    lat: workshop.lat ?? null,
    lng: workshop.lng ?? null,
    servicesOffered: workshop.servicesOffered || [],
    image: workshop.image || null,
    active: workshop.active !== false,
    updatedAt: Date.now(),
  };
  if (workshopId) {
    await set(workshopRef(db, uid, workshopId), { ...payload, createdAt: workshop.createdAt || Date.now() });
    return workshopId;
  }
  const newRef = push(workshopsRef(db, uid));
  await set(newRef, { ...payload, createdAt: Date.now() });
  return newRef.key;
};

export const deleteWorkshop = async (db, uid, workshopId) => {
  if (!db || !uid || !workshopId) return;
  await remove(workshopRef(db, uid, workshopId));
};

export const saveWorkshopSolution = async (db, uid, workshopId, solution, solutionId = null) => {
  if (!db || !uid || !workshopId) throw new Error('missing_ids');
  const payload = {
    vehiclePlate: (solution.vehiclePlate || '').toUpperCase(),
    vehicleMake: solution.vehicleMake || '',
    solutionType: solution.solutionType || 'other',
    title: solution.title || '',
    notes: solution.notes || '',
    cost: solution.cost || '',
    garageVehicleId: solution.garageVehicleId || null,
    updatedAt: Date.now(),
  };
  if (solutionId) {
    await set(ref(db, `users/${uid}/vehicleWorkshops/${workshopId}/solutions/${solutionId}`), {
      ...payload,
      createdAt: solution.createdAt || Date.now(),
    });
    return solutionId;
  }
  const newRef = push(solutionsRef(db, uid, workshopId));
  await set(newRef, { ...payload, createdAt: Date.now() });
  return newRef.key;
};

export const deleteWorkshopSolution = async (db, uid, workshopId, solutionId) => {
  if (!db || !uid || !workshopId || !solutionId) return;
  await remove(ref(db, `users/${uid}/vehicleWorkshops/${workshopId}/solutions/${solutionId}`));
};

export const uploadWorkshopImage = async (storage, uid, workshopId, blob) => {
  if (!storage || !uid || !workshopId || !blob) return null;
  const path = workshopImageRef(storage, uid, workshopId);
  await uploadBytesResumable(path, blob);
  return getDownloadURL(path);
};

export const solutionTypeLabel = (typeId, t) => {
  const item = VEHICLE_SOLUTION_TYPES.find((s) => s.id === typeId);
  return item ? t(item.labelKey, { defaultValue: typeId }) : t('workshop_solution_other', { defaultValue: 'Otro' });
};

export const businessTypeLabel = (typeId, t) => {
  const item = WORKSHOP_BUSINESS_TYPES.find((b) => b.id === typeId);
  return item ? t(item.labelKey, { defaultValue: typeId }) : t('workshop_type_other', { defaultValue: 'Otro' });
};
